/* ==========================================================
   locations-finder.js
   Depends on: window.GBClinics (set by locations-finder.liquid)
   Map: Leaflet (loaded via CDN in section, window.L)
   Geocoding: postcodes.io (free UK API, no key)
   ========================================================== */

(function () {
  'use strict';

  /* ── WAIT FOR LEAFLET ── */
  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ── CONSTANTS ── */
  var PAGE_SIZE = 8;

  /* ── STATE ── */
  var userLat = null;
  var userLng = null;
  var sortedClinics = [];
  var currentFilter = 'blood';
  var visibleCount = PAGE_SIZE;
  var currentLabel = '';
  var leafletMap = null;
  var markers = {};  // id → L.marker
  var userMarker = null;   // 'Your location' pin
  var mode = 'browse';     // 'browse' (regions + all-clinic dots) | 'results' (search)
  var overviewLayer = null; // layer group of small dots, desktop browse mode
  var overviewDots = {};    // id → L.marker (small pin)
  var resultDotLayer = null; // small dots for matching clinics beyond the listed ones
  var regionBuilt = {};     // area → true once its cards are rendered
  var MOBILE_MQ = window.matchMedia('(max-width: 820px)');
  var REGION_ORDER = [
    'Scotland', 'Northern Ireland', 'North East', 'North West', 'Yorkshire and the Humber',
    'East Midlands', 'West Midlands', 'Wales', 'East of England', 'London',
    'South East', 'South West', 'Guernsey'
  ];
  var lastBounds = null;   // bounds to (re)fit — user pin + nearest clinics

  /* ── HAVERSINE DISTANCE (miles) ── */
  function calcDistance(lat1, lng1, lat2, lng2) {
    var R = 3958.8;
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLng = (lng2 - lng1) * Math.PI / 180;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
          + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180)
          * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /* ── TODAY'S HOURS ── */
  function getTodayHours(hours) {
    var days     = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    var dayShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var today    = new Date().getDay();
    var parsed;

    try {
      parsed = typeof hours === 'string' ? JSON.parse(hours) : hours;
    } catch (e) {
      return { isOpen: false, text: 'Hours unavailable' };
    }

    if (!parsed || typeof parsed !== 'object' || Object.keys(parsed).length === 0) {
      return { isOpen: null, text: 'Hours not available' };
    }

    // Lookup helper: match a day key case-insensitively against the parsed object
    function findDay(dayIndex) {
      var fullName  = days[dayIndex];
      var shortName = fullName.substring(0, 3);
      var keys = Object.keys(parsed);
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i].toLowerCase();
        if (k === fullName || k === shortName || k.indexOf(shortName) === 0) {
          return parsed[keys[i]];
        }
      }
      return null;
    }

    var fmt = function (t) {
      var parts = String(t).split(':');
      var hh = parseInt(parts[0], 10);
      var mm = parseInt(parts[1] || '0', 10);
      var suffix = hh >= 12 ? 'pm' : 'am';
      var h12 = hh > 12 ? hh - 12 : hh === 0 ? 12 : hh;
      return mm === 0 ? h12 + suffix : h12 + ':' + String(mm).padStart(2, '0') + suffix;
    };

    var todayData = findDay(today);

    // Today is open
    if (todayData && todayData.open && todayData.close) {
      var now = new Date();
      var cur   = now.getHours() * 100 + now.getMinutes();
      var open  = parseInt(String(todayData.open).replace(':', ''), 10);
      var close = parseInt(String(todayData.close).replace(':', ''), 10);
      var isCurrentlyOpen = cur >= open && cur < close;

      return {
        isOpen: isCurrentlyOpen,
        text: (isCurrentlyOpen ? 'Open today: ' : 'Today: ') + fmt(todayData.open) + ' – ' + fmt(todayData.close)
      };
    }

    // Today is closed — find next open day
    for (var offset = 1; offset <= 7; offset++) {
      var nextIdx = (today + offset) % 7;
      var nextData = findDay(nextIdx);
      if (nextData && nextData.open && nextData.close) {
        var label = (offset === 1)
          ? 'Open tomorrow: '
          : 'Open ' + dayShort[nextIdx] + ': ';
        return {
          isOpen: false,
          text: label + fmt(nextData.open) + ' – ' + fmt(nextData.close)
        };
      }
    }

    // No days open in the whole week
    return { isOpen: null, text: 'By appointment only' };
  }

  /* ── SERVICE TAGS ── */
  function getServiceTags(testType) {
    if (!testType) return [];
    var t = testType.toLowerCase();
    var tags = [];
    if (t.indexOf('venous') !== -1 || t.indexOf('blood') !== -1) tags.push('blood');
    if (t.indexOf('ear') !== -1 || t.indexOf('microsuction') !== -1) tags.push('ear');
    if (t.indexOf('mri') !== -1) tags.push('mri');
    return tags;
  }

  var svcLabel = { blood: '🩸 Blood Tests', ear: '👂 Ear Wax Removal', mri: '🦲 MRI Scan' };
  var svcClass = { blood: 'lf-svc--blood', ear: 'lf-svc--ear', mri: 'lf-svc--mri' };

  function cleanTitle(title) {
    return String(title || '').replace(/^Location\s+/i, '');
  }
  // Customer-facing name: "Warminster Clinic - Ear to Clear" -> "Warminster Clinic" (host-business suffix put people
  // off booking, George 28 Sep 2026). cleanTitle() keeps the full name for the search beacon / ops.
  function displayTitle(title) {
    var t = cleanTitle(title);
    var m = t.match(/^(.*?\S)\s+(?:-|\u2013|\u2014|\|)\s+/);
    return m ? m[1] : t;
  }

  /* ── RENDER CARD ── */
  function renderCard(clinic) {
    var h = getTodayHours(clinic.hours);
    var dist = typeof clinic._dist === 'number'
      ? (clinic._dist < 0.1 ? 'Nearby' : clinic._dist.toFixed(1) + ' mi')
      : '–';

    var tags = getServiceTags(clinic.testType);
    var svcsHTML = tags.map(function (t) {
      return '<span class="lf-svc ' + svcClass[t] + '">' + svcLabel[t] + '</span>';
    }).join('');

    var hoursClass = h.isOpen === true ? 'lf-card__hours--open'
                  : h.isOpen === false ? 'lf-card__hours--closed'
                  : 'lf-card__hours--unknown';

    var el = document.createElement('li');
    el.className = 'lf-card';
    el.setAttribute('role', 'listitem');
    el.dataset.id = clinic.id;
    el.innerHTML =
      '<div class="lf-card__header">' +
        '<h3 class="lf-card__name">' + escHtml(displayTitle(clinic.title)) + '</h3>' +
        '<span class="lf-card__dist">' + dist + '</span>' +
      '</div>' +
      '<p class="lf-card__address">' + escHtml(clinic.address || '') + '</p>' +
      '<div class="lf-card__hours ' + hoursClass + '">' +
        '<span class="lf-card__hours-dot"></span>' +
        '<span class="lf-card__hours-text">' + escHtml(h.text) + '</span>' +
      '</div>' +
      '<div class="lf-card__services">' + svcsHTML + '</div>' +
      '<div class="lf-card__actions">' +
        '<a href="' + clinic.url + '" class="lf-btn lf-btn--book">Book Now →</a>' +
        '<a href="https://maps.google.com/?q=' + encodeURIComponent(clinic.address || '') + '"' +
           ' class="lf-btn lf-btn--dir" target="_blank" rel="noopener">📍 Directions</a>' +
      '</div>';

    el.addEventListener('mouseenter', function () { highlightPin(clinic.id, true); });
    el.addEventListener('mouseleave', function () { highlightPin(clinic.id, false); });
    el.addEventListener('click', function (e) {
      if (e.target.tagName === 'A') return;
      panToClinic(clinic);
    });

    return el;
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── FILTER + RENDER ── */
  function filteredClinics() {
    if (currentFilter === 'all') return sortedClinics;
    return sortedClinics.filter(function (c) {
      return getServiceTags(c.testType).indexOf(currentFilter) !== -1;
    });
  }

  function renderResults() {
    var listEl     = document.getElementById('lf-list');
    var countEl    = document.getElementById('lf-count');
    var moreWrap   = document.getElementById('lf-show-more');
    var moreBtn    = document.getElementById('lf-more-btn');

    listEl.innerHTML = '';

    var pool  = filteredClinics();
    var slice = pool.slice(0, visibleCount);

    if (pool.length === 0) {
      listEl.innerHTML =
        '<div class="lf-no-results">' +
          '<strong>No clinics found</strong>' +
          'Try a different postcode or remove a service filter.' +
        '</div>';
      moreWrap.style.display = 'none';
      countEl.textContent = 'No results';
      updateMapMarkers([]);
      return;
    }

    slice.forEach(function (c) { listEl.appendChild(renderCard(c)); });

    var remaining = pool.length - visibleCount;
    if (remaining > 0) {
      moreWrap.style.display = 'block';
      moreBtn.textContent = 'Show ' + Math.min(remaining, PAGE_SIZE) + ' more clinics';
    } else {
      moreWrap.style.display = 'none';
    }

    var near = currentLabel ? ' near ' + currentLabel : '';
    countEl.textContent =
      'Showing ' + slice.length + ' of ' + pool.length + ' clinics' + near;

    // Listed clinics (nearest N, grows with "Show more") get full pins and set
    // the initial framing. Every other matching clinic is a small dot, so
    // zooming out reveals the wider network instead of an empty map.
    updateMapMarkers(slice);
    updateResultDots(pool.slice(visibleCount));
  }

  /* ── MAP ── */
  /* One pin style for every clinic. Listed clinics use the full size; the
     rest of the network uses the same pin at a smaller size. Hover = navy. */
  function makeIcon(highlighted, small) {
    var fill = highlighted ? '#171A2E' : '#52B27F';
    var w = small ? 12 : 18, h = small ? 15 : 23;
    var svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + w + '" height="' + h + '" viewBox="0 0 28 36">' +
        '<path d="M14 0C6.27 0 0 6.27 0 14c0 9.58 14 22 14 22S28 23.58 28 14C28 6.27 21.73 0 14 0z"' +
             ' fill="' + fill + '"/>' +
        '<circle cx="14" cy="14" r="6" fill="white"/>' +
      '</svg>';
    return window.L.divIcon({
      html: svg,
      className: '',
      iconSize: [w, h],
      iconAnchor: [w / 2, h],
      popupAnchor: [0, -(h + 1)]
    });
  }

  function makeUserIcon() {
    return window.L.divIcon({
      html: '<span class="lf-you-pin"></span>',
      className: '',
      iconSize: [18, 18],
      iconAnchor: [9, 9],
      tooltipAnchor: [0, -12]
    });
  }

  /* Fit the map to the user's pin plus the nearest clinics so at least one
     clinic is always visible alongside "Your location". */
  function fitToResults() {
    if (!leafletMap || !lastBounds) return;
    lockToUK();
    leafletMap.fitBounds(lastBounds, { padding: [40, 40], maxZoom: 13 });
  }

  function ensureMap() {
    if (leafletMap || !window.L) return leafletMap;
    leafletMap = window.L.map('lf-map', {
      zoomControl: true,
      scrollWheelZoom: true,
      maxBounds: UK_PAN_BOUNDS,      // can't pan away from the UK
      maxBoundsViscosity: 1.0
    });
    leafletMap.on('resize', lockToUK); // re-lock whenever the panel changes size
    window.L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=cb1_2bg3_1_86b52f35b3800e0090776f0d',
      { attribution: '© OpenStreetMap © CARTO', subdomains: 'abcd', maxZoom: 20 }
    ).addTo(leafletMap);
    return leafletMap;
  }

  function popupHtml(c) {
    return '<p class="lf-popup__name">' + escHtml(displayTitle(c.title)) + '</p>' +
           '<p class="lf-popup__addr">' + escHtml(c.address || '') + '</p>' +
           (c.url ? '<a class="lf-popup__link" href="' + escHtml(c.url) + '">View clinic &rarr;</a>' : '');
  }

  /* ── BROWSE MODE: all clinics as small pins (desktop, before a search) ── */
  var UK_BOUNDS = [[49.85, -8.2], [58.75, 1.8]];
  var UK_PAN_BOUNDS = [[48.5, -12.0], [61.5, 4.5]]; // a little slack around the UK for panning

  /* Lock the minimum zoom to the level that shows the full UK in the current
     panel, so visitors can't zoom out to Europe / the world. */
  function lockToUK() {
    if (!leafletMap) return;
    var size = leafletMap.getSize();
    if (!size.x || !size.y) return; // panel hidden (mobile before "Show Map")
    var z = leafletMap.getBoundsZoom(UK_BOUNDS, false);
    leafletMap.setMinZoom(z);
    if (leafletMap.getZoom() < z) leafletMap.setZoom(z);
  }

  function initOverviewMap() {
    if (mode !== 'browse' || overviewLayer || !ensureMap()) return;
    overviewLayer = window.L.layerGroup().addTo(leafletMap);
    (window.GBClinics || []).forEach(function (c) {
      var d = window.L.marker([c.lat, c.lng], { icon: makeIcon(false, true) })
        .bindPopup(popupHtml(c))
        .addTo(overviewLayer);
      d.on('click', function () {
        var card = document.querySelector('.lf-card[data-id="' + c.id + '"]');
        if (card && card.offsetParent) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          card.classList.add('lf-card--highlighted');
          setTimeout(function () { card.classList.remove('lf-card--highlighted'); }, 1800);
        }
      });
      overviewDots[c.id] = d;
    });
    fitUK();
    // Panel height comes from CSS applied after first paint — re-measure and re-fit
    setTimeout(function () { if (mode === 'browse' && leafletMap) { leafletMap.invalidateSize(); fitUK(); } }, 150);
  }

  function fitUK() {
    if (!leafletMap) return;
    lockToUK();
    leafletMap.fitBounds(UK_BOUNDS, { padding: [10, 10] });
  }

  function updateResultDots(clinics) {
    if (!leafletMap) return;
    if (resultDotLayer) { leafletMap.removeLayer(resultDotLayer); resultDotLayer = null; }
    if (!clinics.length) return;
    resultDotLayer = window.L.layerGroup().addTo(leafletMap);
    clinics.forEach(function (c) {
      window.L.marker([c.lat, c.lng], { icon: makeIcon(false, true), zIndexOffset: -100 })
        .bindPopup(popupHtml(c))
        .addTo(resultDotLayer);
    });
  }

  function clearOverview() {
    if (overviewLayer && leafletMap) leafletMap.removeLayer(overviewLayer);
    overviewLayer = null;
    overviewDots = {};
  }

  /* ── REGION ACCORDIONS (left column, browse mode) ── */
  function buildRegions() {
    var wrap = document.getElementById('lf-regions');
    if (!wrap) return;
    var clinics = window.GBClinics || [];
    var groups = {};
    clinics.forEach(function (c) {
      var a = (c.area || '').trim() || 'Other';
      (groups[a] = groups[a] || []).push(c);
    });
    var names = Object.keys(groups).sort(function (a, b) {
      var ia = REGION_ORDER.indexOf(a), ib = REGION_ORDER.indexOf(b);
      if (ia === -1) ia = 99; if (ib === -1) ib = 99;
      return ia - ib || a.localeCompare(b);
    });

    names.forEach(function (name) {
      var list = groups[name].slice().sort(function (a, b) {
        return displayTitle(a.title).localeCompare(displayTitle(b.title));
      });
      var item = document.createElement('div');
      item.className = 'lf-region';
      item.innerHTML =
        '<button class="lf-region__head" type="button" aria-expanded="false">' +
          '<span class="lf-region__name">' + escHtml(name) + '</span>' +
          '<span class="lf-region__count">' + list.length + (list.length === 1 ? ' clinic' : ' clinics') + '</span>' +
          '<span class="lf-region__chev" aria-hidden="true"></span>' +
        '</button>' +
        '<ul class="lf-region__body" role="list" hidden></ul>';
      var head = item.querySelector('.lf-region__head');
      var body = item.querySelector('.lf-region__body');

      head.addEventListener('click', function () {
        var open = head.getAttribute('aria-expanded') === 'true';
        // close all
        wrap.querySelectorAll('.lf-region__head').forEach(function (h) { h.setAttribute('aria-expanded', 'false'); });
        wrap.querySelectorAll('.lf-region__body').forEach(function (b) { b.hidden = true; });
        if (open) {
          fitUK();
          return;
        }
        if (!regionBuilt[name]) {
          list.forEach(function (c) { body.appendChild(renderCard(c)); });
          regionBuilt[name] = true;
        }
        head.setAttribute('aria-expanded', 'true');
        body.hidden = false;
        if (leafletMap && list.length) {
          var b = window.L.latLngBounds(list.map(function (c) { return [c.lat, c.lng]; }));
          leafletMap.fitBounds(b, { padding: [30, 30], maxZoom: 11 });
        }
      });
      wrap.appendChild(item);
    });
  }

  function enterBrowseMode() {
    var results = document.getElementById('lf-results');
    if (!results) return;
    results.classList.add('lf-visible', 'lf-mode-browse');
    results.classList.remove('lf-mode-results');
    // Map only matters on desktop (hidden on mobile until a search)
    if (!MOBILE_MQ.matches) {
      if (window.L) initOverviewMap();
      else document.querySelector('script[src*="leaflet"]').addEventListener('load', initOverviewMap);
    }
  }

  function initMap(lat, lng) {
    if (!ensureMap()) return;
    clearOverview();

    leafletMap.setView([lat, lng], 10);

    // "Your location" pin at the searched postcode / town / GPS position
    if (!userMarker) {
      userMarker = window.L.marker([lat, lng], { icon: makeUserIcon(), zIndexOffset: 1000 })
        .addTo(leafletMap)
        .bindTooltip('Your location', { permanent: true, direction: 'top', className: 'lf-you-tooltip' });
    } else {
      userMarker.setLatLng([lat, lng]);
    }
  }

  function updateMapMarkers(clinics) {
    if (!leafletMap) return;

    var newIds = {};
    clinics.forEach(function (c) { newIds[c.id] = true; });

    var changed = false;

    // Remove stale markers
    Object.keys(markers).forEach(function (id) {
      if (!newIds[id]) {
        leafletMap.removeLayer(markers[id]);
        delete markers[id];
        changed = true;
      }
    });

    // Add new markers
    clinics.forEach(function (c) {
      if (markers[c.id]) return;

      var m = window.L.marker([c.lat, c.lng], { icon: makeIcon(false) })
        .addTo(leafletMap)
        .bindPopup(popupHtml(c));

      m.on('click', function () {
        var card = document.querySelector('.lf-card[data-id="' + c.id + '"]');
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          card.classList.add('lf-card--highlighted');
          setTimeout(function () { card.classList.remove('lf-card--highlighted'); }, 1800);
        }
      });

      markers[c.id] = m;
      changed = true;
    });

    // Only fit bounds when the marker set actually changed (e.g. initial load or filter change).
    // Repeated searches with the same postcode leave markers unchanged so we skip fitBounds,
    // letting initMap's setView keep the map centred on the searched location.
    if (changed && clinics.length > 0) {
      var pts = clinics.slice(0, 3).map(function (c) { return [c.lat, c.lng]; });
      if (userLat !== null && userLng !== null) pts.push([userLat, userLng]);
      lastBounds = window.L.latLngBounds(pts);
      fitToResults();
    }
  }

  function highlightPin(id, on) {
    if (markers[id]) markers[id].setIcon(makeIcon(on));
    if (overviewDots[id]) {
      overviewDots[id].setIcon(makeIcon(on, !on));
      overviewDots[id].setZIndexOffset(on ? 500 : 0);
    }
  }

  function panToClinic(clinic) {
    if (!leafletMap) return;
    leafletMap.setView([clinic.lat, clinic.lng], 14);
    if (markers[clinic.id]) markers[clinic.id].openPopup();
  }

  /* ── SHOW RESULTS PANEL ── */
  function showResultsPanel() {
    var steps   = document.getElementById('lf-steps');
    var results = document.getElementById('lf-results');
    var section = document.querySelector('.lf-section');
    mode = 'results';
    if (steps)   steps.style.display = 'none';
    if (section) section.classList.add('lf-searched');
    if (results) {
      results.classList.add('lf-visible', 'lf-mode-results');
      results.classList.remove('lf-mode-browse');
    }
  }

  /* ── RUN SEARCH ── */
  function runSearch(lat, lng, label, kind) {
    userLat = lat;
    userLng = lng;
    currentLabel = label;
    visibleCount = PAGE_SIZE;

    var clinics = window.GBClinics || [];

    sortedClinics = clinics
      .map(function (c) {
        return Object.assign({}, c, { _dist: calcDistance(lat, lng, c.lat, c.lng) });
      })
      .sort(function (a, b) { return a._dist - b._dist; });

    logSearch(kind || 'postcode', label, lat, lng);

    showResultsPanel();
    setSortLabel('Sorted by distance');

    // Leaflet may not be ready if deferred — wait for it
    if (window.L) {
      initMap(lat, lng);
    } else {
      document.querySelector('script[src*="leaflet"]').addEventListener('load', function () {
        initMap(lat, lng);
      });
    }

    renderResults();
  }

  function setSortLabel(text) {
    var el = document.getElementById('lf-sort-label');
    if (el) el.textContent = text;
  }

  /* ── COVERAGE BEACON ──
     Tells the Hub where people search so the outbound team can see coverage
     gaps. Sends only an outward code or town name (never a full postcode),
     coordinates rounded to ~1 km, and the distance to the nearest clinic.
     Fire-and-forget; never affects the page. */
  var CAPTURE_URL = 'https://partners.goodbodyclinic.com/api/coverage/capture';

  function logSearch(kind, label, lat, lng) {
    try {
      if (window.Shopify && window.Shopify.designMode) return; // theme editor
      var pool = filteredClinics();
      var nearest = pool.length ? pool[0] : (sortedClinics[0] || null);
      var q = String(label || '');
      if (kind === 'postcode' || kind === 'outcode') {
        var m = q.toUpperCase().replace(/\s+/g, '').match(/^([A-Z]{1,2}\d[A-Z\d]?)/);
        q = m ? m[1] : q;
      }
      var payload = JSON.stringify({
        q: q,
        kind: kind,
        lat: Math.round(lat * 100) / 100,
        lng: Math.round(lng * 100) / 100,
        nearest: nearest ? Math.round(nearest._dist * 10) / 10 : null,
        nearestName: nearest ? cleanTitle(nearest.title) : '',
        filter: currentFilter,
        device: MOBILE_MQ.matches ? 'mobile' : 'desktop',
        src: 'locations'
      });
      if (navigator.sendBeacon) navigator.sendBeacon(CAPTURE_URL, payload);
      else fetch(CAPTURE_URL, { method: 'POST', body: payload, keepalive: true, mode: 'no-cors' });
    } catch (e) { /* never break the page for analytics */ }
  }

  /* ── POSTCODES.IO GEOCODE (free, no key) ──
     Order: full postcode → outward code (e.g. "BA1") → place name
     (town / city / village via OS Open Names). */
  function looksLikePostcode(pc) {
    return /^[A-Z]{1,2}\d[A-Z\d]?(\d[A-Z]{2})?$/.test(pc);
  }

  var PLACE_RANK = { 'City': 0, 'Town': 1, 'Suburban Area': 2, 'Village': 3, 'Hamlet': 4, 'Other Settlement': 5 };

  function geocodePlace(raw, callback) {
    var q = raw.trim();
    fetch('https://api.postcodes.io/places?q=' + encodeURIComponent(q) + '&limit=10')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var list = (data.status === 200 && data.result) ? data.result : [];
        if (!list.length) {
          callback(new Error('We couldn\u2019t find that postcode or town. Please check and try again.'));
          return;
        }
        // Prefer an exact name match, then bigger places (city > town > village)
        var ql = q.toLowerCase();
        list.sort(function (a, b) {
          var ea = (a.name_1 || '').toLowerCase() === ql ? 0 : 1;
          var eb = (b.name_1 || '').toLowerCase() === ql ? 0 : 1;
          if (ea !== eb) return ea - eb;
          var ra = PLACE_RANK[a.local_type] !== undefined ? PLACE_RANK[a.local_type] : 9;
          var rb = PLACE_RANK[b.local_type] !== undefined ? PLACE_RANK[b.local_type] : 9;
          return ra - rb;
        });
        var best = list[0];
        callback(null, { lat: best.latitude, lng: best.longitude, label: best.name_1 || q, kind: 'place' });
      })
      .catch(function () {
        callback(new Error('Could not reach the location service. Please try again.'));
      });
  }

  function geocodePostcode(raw, callback) {
    var pc = raw.trim().toUpperCase().replace(/\s+/g, '');

    // Not postcode-shaped (e.g. "Bath", "Milton Keynes") → straight to place lookup
    if (!looksLikePostcode(pc)) { geocodePlace(raw, callback); return; }

    fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(pc))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status === 200) {
          callback(null, { lat: data.result.latitude, lng: data.result.longitude, label: raw.trim(), kind: 'postcode' });
          return;
        }
        // Try outward code (e.g. "AB10")
        var outward = pc.replace(/\d[A-Z]{2}$/, '');
        return fetch('https://api.postcodes.io/outcodes/' + encodeURIComponent(outward))
          .then(function (r2) { return r2.json(); })
          .then(function (d2) {
            if (d2.status === 200) {
              callback(null, { lat: d2.result.latitude, lng: d2.result.longitude, label: raw.trim(), kind: 'outcode' });
            } else {
              geocodePlace(raw, callback); // last resort: maybe it was a short place name
            }
          });
      })
      .catch(function () {
        callback(new Error('Could not reach the location service. Please try again.'));
      });
  }

  /* ── UI WIRING ── */
  function showError(msg) {
    var el = document.getElementById('lf-search-error');
    if (!el) return;
    el.textContent = msg;
    el.style.display = msg ? 'block' : 'none';
  }

  function setSearchLoading(loading) {
    var btn = document.getElementById('lf-search-btn');
    if (!btn) return;
    btn.textContent = loading ? 'Searching…' : (btn.dataset.label || 'Search');
    btn.classList.toggle('lf-loading', loading);
  }

  onReady(function () {
    var postcodeInput = document.getElementById('lf-postcode');
    var searchBtn     = document.getElementById('lf-search-btn');
    var geoBtn        = document.getElementById('lf-geo-btn');
    var moreBtn       = document.getElementById('lf-more-btn');
    var mapToggle     = document.getElementById('lf-map-toggle');
    var listBtn       = document.getElementById('lf-list-btn');
    var resultsEl     = document.getElementById('lf-results');

    if (!searchBtn) return; // section not on page

    // Store original label
    searchBtn.dataset.label = searchBtn.textContent;

    // Search on button click
    searchBtn.addEventListener('click', function () {
      var val = postcodeInput.value.trim();
      showError('');
      if (!val) { showError('Please enter a postcode or town.'); return; }

      setSearchLoading(true);
      geocodePostcode(val, function (err, result) {
        setSearchLoading(false);
        if (err) { showError(err.message); return; }
        runSearch(result.lat, result.lng, result.label, result.kind);
      });
    });

    // Search on Enter key
    postcodeInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') searchBtn.click();
    });

    // Geolocation
    geoBtn.addEventListener('click', function () {
      showError('');
      if (!navigator.geolocation) {
        showError('Geolocation is not supported by your browser.');
        return;
      }

      var original = geoBtn.textContent;
      geoBtn.textContent = '📍 Locating…';

      navigator.geolocation.getCurrentPosition(
        function (pos) {
          geoBtn.textContent = original;
          runSearch(pos.coords.latitude, pos.coords.longitude, 'your location', 'geo');
        },
        function () {
          geoBtn.textContent = original;
          showError('Could not access your location. Please enter a postcode instead.');
        }
      );
    });

    // Filter chips
    document.querySelectorAll('.lf-chip').forEach(function (chip) {
      chip.addEventListener('click', function () {
        document.querySelectorAll('.lf-chip').forEach(function (c) {
          c.classList.remove('lf-chip--active');
        });
        chip.classList.add('lf-chip--active');
        currentFilter = chip.dataset.filter;
        visibleCount = PAGE_SIZE;
        renderResults();
      });
    });

    // Show more
    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        visibleCount += PAGE_SIZE;
        renderResults();
      });
    }

    // Mobile map toggle
    function showMap() {
      resultsEl.classList.add('lf-map-active');
      if (listBtn) listBtn.classList.add('lf-list-btn--visible');
      if (mapToggle) mapToggle.setAttribute('aria-expanded', 'true');
      if (leafletMap) setTimeout(function () { leafletMap.invalidateSize(); fitToResults(); }, 50);
    }

    function showList() {
      resultsEl.classList.remove('lf-map-active');
      if (listBtn) listBtn.classList.remove('lf-list-btn--visible');
      if (mapToggle) mapToggle.setAttribute('aria-expanded', 'false');
    }

    if (mapToggle) {
      mapToggle.addEventListener('click', function () {
        resultsEl.classList.contains('lf-map-active') ? showList() : showMap();
      });
    }

    if (listBtn) {
      listBtn.addEventListener('click', showList);
    }

    // Before any search: desktop shows the "how it works" strip, regional
    // accordions and every clinic as a small dot on the map; mobile shows only
    // the strip (Clarity showed mobile visitors tapping the first A–Z card
    // instead of finding their nearest clinic). A search switches to results.
    buildRegions();
    enterBrowseMode();
    var mqHandler = function () { if (mode === 'browse') enterBrowseMode(); };
    if (MOBILE_MQ.addEventListener) MOBILE_MQ.addEventListener('change', mqHandler);
    else if (MOBILE_MQ.addListener) MOBILE_MQ.addListener(mqHandler);
  }); // end onReady

}());
